import { Component, Input, OnInit, ViewChildren, ElementRef, QueryList, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";

import { ChangePasswordBaseComponent } from "@mypxplat/xplat/features";
import { UserService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { fetchAuthSession, updatePassword } from "aws-amplify/auth";
import { AppService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { catchError, take } from "rxjs/operators";
import { of } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-change-password",
  templateUrl: "change-password.component.html",
  standalone: false,
})
export class ChangePasswordComponent extends ChangePasswordBaseComponent implements OnInit {
  @Input() code: string;
  @Input() creatingNewFenderPassword: boolean = false;
  @Output() fenderPasswordSelected: EventEmitter<any> = new EventEmitter();
  public passwordPasses: boolean = false;
  public passwordVisible: boolean;
  public newFenderPassword: string;
  public newFenderPasswordConfirm: string;
  public passwordTouched: boolean = false;
  public passwordFocused: boolean = false;

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public appService: AppService,
    private translate: TranslateService,
    private router: Router
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.code) {
      this.passwordArgs.code = this.code;
    } else {
      this.passwordArgs.old_password = "";
    }
  }

  checkPassword() {
    this.passwordPasses = this.userService.passwordTester.test(this.creatingNewFenderPassword ? this.newFenderPassword : this.passwordArgs.new_password);
    return this.passwordPasses;
  }

  passwordBlur() {
    this.passwordFocused = false;
    this.passwordTouched = true;
    this.checkPassword();
  }

  passwordFocus() {
    this.passwordFocused = true;
  }

  get activeNewPassword(): string {
    return this.creatingNewFenderPassword ? this.newFenderPassword : (this.passwordArgs?.new_password ?? "");
  }

  get showPasswordError(): boolean {
    return this.passwordTouched && !this.passwordFocused && !!this.activeNewPassword && !this.passwordPasses;
  }

  submit() {
    if (this.creatingNewFenderPassword) {
      if (!this.newFenderPassword || this.newFenderPassword.length < 12) {
        this.appService.alert(this.translate.instant("login.password_min_12_chars"));
      } else if (this.newFenderPassword != this.newFenderPasswordConfirm) {
        this.appService.alert(this.translate.instant("login.new_passwords_not_match"));
      } else if (!this.checkPassword()) {
        this.appService.alert(this.translate.instant("login.password_does_not_meet_criteria"));
      } else {
        this.fenderPasswordSelected.emit(this.newFenderPassword);
      }
    } else {
      if (this.passwordArgs.new_password.length < 12) {
        this.appService.alert(this.translate.instant("login.password_min_12_chars"));
      } else if (this.passwordArgs.new_password != this.passwordArgs.new_password_confirm) {
        this.appService.alert(this.translate.instant("login.new_passwords_not_match"));
      } else if (!this.checkPassword()) {
        this.appService.alert(this.translate.instant("login.password_does_not_meet_criteria"));
      } else {
        updatePassword({
          newPassword: this.passwordArgs.new_password,
          oldPassword: this.passwordArgs.old_password,
        })
          .then(() => {
            this.userService
              .signOutCognitoUserGlobally()
              .pipe(catchError(() => of(null)))
              .subscribe(() => {
                this.handlePasswordChanged();
              });
          })
          .catch((error) => {
            this.appService.alertError(error);
          });
      }
    }
  }

  private handlePasswordChanged() {
    this.activeModal.close();
    const modalRef = this.appService.alert(this.translate.instant("login.password_changed_success"));
    modalRef.closed.pipe(take(1)).subscribe(() => this.performLogoutAfterPasswordChange());
    modalRef.dismissed.pipe(take(1)).subscribe(() => this.performLogoutAfterPasswordChange());
  }

  private performLogoutAfterPasswordChange() {
    this.appService.logout().subscribe({
      next: () => {
        this.router.navigate(["/logout"]);
      },
      error: (error) => {
        this.appService.alertError(error);
      },
    });
  }
}
